import { useCallback, useEffect, useRef, useState } from "react";
// REDUX
import { useSelector } from "react-redux";
import { RootState } from "../../app/store";
// ALL BANKS
import {
  AllBanksBank,
  AllBanksMergeMode,
  isAllBanks,
  mapWithConcurrency,
  mergeBankResponses,
  perBankBody,
} from "./allBanks";

/** How many per-bank requests an all-banks report keeps in flight. */
export const ALL_BANKS_CONCURRENCY = 4;

interface MutationResultLike {
  data?: any;
  isLoading: boolean;
  isError: boolean;
  error?: any;
}

interface FanOutState {
  active: boolean;
  data: any;
  isLoading: boolean;
  isError: boolean;
  error: any;
}

const IDLE: FanOutState = {
  active: false,
  data: undefined,
  isLoading: false,
  isError: false,
  error: undefined,
};

const settle = (pending: any): Promise<any> =>
  pending && typeof pending.unwrap === "function" ? pending.unwrap() : Promise.resolve(pending);

/**
 * Wraps a report's RTK mutation hook so the page can run it for "All banks".
 *
 *   const [trigger, { data, isLoading, isError, error }] =
 *     useAllBanksMutation(usePostFaultLogReportMutation(), "rows");
 *
 * A trigger whose `body.bank_id` is a real bank goes straight to the mutation
 * and the mutation's own result is returned. A trigger with `ALL_BANKS_ID`
 * runs the mutation once per bank in `filters.banks` and returns the merged
 * answer in the same `{ data, isLoading, isError, error }` shape. A newer
 * trigger supersedes an older fan-out still in flight.
 *
 * The bank list can change while "All banks" stays selected (the lift-type
 * filter or the monitoring selection narrows it). The last all-banks request
 * is then run again over the new list, so the page never shows banks that are
 * no longer in scope; a page does not have to watch `filters.banks` itself.
 */
function useAllBanksMutation<R extends MutationResultLike>(
  mutation: readonly [(arg: any) => any, R],
  mode: AllBanksMergeMode,
): [(arg: { body: any; token: string }) => Promise<void>, R] {
  const [trigger, result] = mutation;
  const banks: AllBanksBank[] = useSelector((state: RootState) => state.report.filters.banks);
  const [fanOut, setFanOut] = useState<FanOutState>(IDLE);
  const generation = useRef(0);
  const lastAllBanksArg = useRef<{ body: any; token: string } | null>(null);
  const seenBanks = useRef(banks);

  const run = useCallback(
    async (arg: { body: any; token: string }) => {
      generation.current += 1;
      const mine = generation.current;
      if (!isAllBanks(arg?.body?.bank_id)) {
        lastAllBanksArg.current = null;
        setFanOut(IDLE);
        await settle(trigger(arg)).catch(() => undefined);
        return;
      }
      lastAllBanksArg.current = arg;
      const targets = Array.isArray(banks) ? banks : [];
      setFanOut({ ...IDLE, active: true, isLoading: true });
      try {
        const answers = await mapWithConcurrency(targets, ALL_BANKS_CONCURRENCY, async (bank) => ({
          bank,
          response: await settle(trigger({ ...arg, body: perBankBody(arg.body, bank.id, mode) })),
        }));
        if (generation.current !== mine) return;
        setFanOut({ ...IDLE, active: true, data: mergeBankResponses(answers, mode, arg.body) });
      } catch (error) {
        if (generation.current !== mine) return;
        setFanOut({ ...IDLE, active: true, isError: true, error });
      }
    },
    [trigger, banks, mode],
  );

  useEffect(() => {
    if (seenBanks.current === banks) return;
    seenBanks.current = banks;
    if (lastAllBanksArg.current) run(lastAllBanksArg.current);
  }, [banks, run]);

  if (!fanOut.active) return [run, result];
  return [
    run,
    {
      ...result,
      data: fanOut.data,
      isLoading: fanOut.isLoading,
      isError: fanOut.isError,
      error: fanOut.error,
    },
  ];
}

export default useAllBanksMutation;
